import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { SurfaceCard } from '../../design/SurfaceCard'
import type { MemberClass, MyClassesResponse } from './types'

/** Fixed hues, the same the gym picks from. Kept in step with ClassesPage. */
const COLOR_BAR: Record<string, string> = {
  blue: 'bg-blue-500',
  purple: 'bg-violet-500',
  green: 'bg-green-600',
  orange: 'bg-orange-500',
  red: 'bg-red-600',
  pink: 'bg-pink-500',
  teal: 'bg-teal-500',
}

/** "Now" at the gym, as the same wall-clock string the classes use. */
export function gymWallClock(now: Date, timezone: string | null): string {
  // sv-SE formats as "2026-10-29 18:04:00", which only needs the T.
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: timezone ?? undefined,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  })
    .format(now)
    .replace(' ', 'T')
}

function addDay(day: string): string {
  const d = new Date(`${day}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() + 1)
  return d.toISOString().slice(0, 10)
}

export interface NextBooking {
  item: MemberClass
  /** First names of the children booked on it; empty when it is only you. */
  childNames: string[]
  day: 'today' | 'tomorrow'
  /** Minutes until it starts, negative once it has. */
  startsIn: number
}

/**
 * The first class someone on this account is booked on, today or tomorrow,
 * that has not ended. Null when there is none.
 */
export function nextBooking(data: MyClassesResponse, now: string): NextBooking | null {
  const today = now.slice(0, 10)
  const tomorrow = addDay(today)
  const upcoming = data.classes
    .filter((c) => c.ends_at > now)
    .filter((c) => {
      const day = c.starts_at.slice(0, 10)
      return day === today || day === tomorrow
    })
    .filter((c) => Object.values(c.people).some((p) => p.booked))
    .sort((a, b) => a.starts_at.localeCompare(b.starts_at))
  const item = upcoming[0]
  if (!item) return null

  const childNames = data.people
    .filter((p) => p.childId && item.people[p.key]?.booked)
    .map((p) => p.firstName ?? '')
    .filter(Boolean)

  // Both strings are the gym's wall clock, so reading them as UTC keeps the
  // difference right whatever zone the phone is in.
  const startsIn = Math.round((Date.parse(`${item.starts_at}Z`) - Date.parse(`${now}Z`)) / 60_000)

  return {
    item,
    childNames,
    day: item.starts_at.slice(0, 10) === today ? 'today' : 'tomorrow',
    startsIn,
  }
}

interface Props {
  booking: NextBooking
  gymSlug: string
}

/**
 * Your next booked class, so nobody has to open the calendar to remember it.
 * The Cursuri tab next to Home stays the place to book and cancel.
 */
export function NextClassCard({ booking, gymSlug }: Props) {
  const { t } = useTranslation()
  const { item, childNames, day, startsIn } = booking
  const soon = startsIn <= 30
  const trainer = item.trainer
    ? [item.trainer.first_name, item.trainer.last_name].filter(Boolean).join(' ') ||
      item.trainer.nickname
    : null

  const when =
    startsIn <= 0
      ? t('classes.nextNow')
      : soon
        ? t('classes.nextInMinutes', { count: startsIn })
        : `${day === 'today' ? t('classes.nextToday') : t('classes.nextTomorrow')}, ${item.starts_at.slice(11, 16)}`

  return (
    <Link to={`/${gymSlug}/classes`} className="block">
      <SurfaceCard className="flex items-stretch gap-3 px-4 py-3">
        <span
          aria-hidden
          className={`w-1.5 shrink-0 rounded-full ${COLOR_BAR[item.color] ?? 'bg-foreground/30'}`}
        />
        <span className="min-w-0 flex-1">
          <span className={`eyebrow block ${soon ? 'text-success' : 'text-foreground/40'}`}>
            {soon ? t('classes.nextSoon') : t('classes.nextTitle')}
          </span>
          <span className="mt-0.5 block truncate text-[15px] font-semibold">{item.title}</span>
          <span className="block truncate text-xs text-foreground/55">
            {[
              when,
              trainer ? t('classes.withTrainer', { name: trainer }) : null,
              childNames.length > 0 ? childNames.join(', ') : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </span>
        <span aria-hidden className="self-center text-foreground/35">
          ›
        </span>
      </SurfaceCard>
    </Link>
  )
}
